import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ExternalLink, Sparkles, ChevronRight } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const STATUSES = [
    { id: "new", label: "New" },
    { id: "reviewing", label: "Reviewing" },
    { id: "replied", label: "Replied" },
    { id: "ignored", label: "Ignored" },
];

// Admin Leads page — works the queue of scraped CSV-pain-point posts.
// Each row is a real person on a real forum complaining about a CSV problem
// OpenCSV already solves. The job is to (1) review, (2) draft a public Q&A
// answer, (3) optionally reply on the source forum, (4) mark replied.

export default function AdminLeads() {
    const [leads, setLeads] = useState([]);
    const [counts, setCounts] = useState({});
    const [statusFilter, setStatusFilter] = useState("new");
    const [minConf, setMinConf] = useState(0.9);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/leads`, { params: { status: statusFilter, min_confidence: minConf } });
            setLeads(data.leads || []);
            setCounts(data.counts || {});
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
        } finally { setLoading(false); }
    }, [statusFilter, minConf]);

    useEffect(() => { refresh(); }, [refresh]);

    const setStatus = async (lead, status) => {
        try {
            await api.patch(`/admin/leads/${lead.lead_id}`, { status });
            toast.success(`Marked ${status}`);
            refresh();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const draftAnswer = async (lead) => {
        // Pre-fill a /ask draft from the lead's question. The admin opens
        // /admin/questions in a new tab and the URL hash carries the prefill.
        const params = new URLSearchParams({
            from_lead: lead.lead_id,
            title: lead.title.slice(0, 200),
            summary: lead.snippet.slice(0, 280),
        });
        window.open(`/admin/questions?${params.toString()}`, "_blank");
    };

    return (
        <>
            <Helmet><title>Admin · Leads · OpenCSV</title></Helmet>
            <section className="px-6 sm:px-12 py-12 bg-paper min-h-screen">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
                        <div>
                            <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted">/ admin · leads</div>
                            <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-1">Lead queue</h1>
                            <p className="text-sm text-ink-soft mt-1">Real people complaining about CSV problems we already solve.</p>
                        </div>
                        <div className="flex gap-2 text-sm">
                            <Link to="/admin" className="border border-ink/20 px-3 py-2" data-testid="admin-leads-back">Users</Link>
                            <Link to="/admin/questions" className="border border-ink/20 px-3 py-2" data-testid="admin-leads-questions-link">Q&A</Link>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap mb-6">
                        {STATUSES.map((s) => (
                            <button
                                key={s.id}
                                onClick={() => setStatusFilter(s.id)}
                                className={`px-3 py-1.5 text-sm border ${statusFilter === s.id ? "bg-ink text-white border-ink" : "border-ink/20 bg-white"}`}
                                data-testid={`leads-tab-${s.id}`}
                            >
                                {s.label} <span className="opacity-60">({counts[s.id] || 0})</span>
                            </button>
                        ))}
                        <span className="ml-4 text-xs text-ink-soft">Min confidence:</span>
                        {[1.0, 0.9, 0.8, 0.7, 0.0].map((c) => (
                            <button
                                key={c}
                                onClick={() => setMinConf(c)}
                                className={`px-2 py-1 text-xs border ${minConf === c ? "bg-cobalt text-white border-cobalt" : "border-ink/15 bg-white"}`}
                                data-testid={`leads-conf-${c.toString().replace(".", "")}`}
                            >
                                {c.toFixed(1)}
                            </button>
                        ))}
                    </div>

                    <div className="bg-white border border-ink/15">
                        {loading ? (
                            <div className="p-8 text-center text-sm text-ink-muted">Loading…</div>
                        ) : leads.length === 0 ? (
                            <div className="p-8 text-center text-sm text-ink-muted">No leads in this view.</div>
                        ) : (
                            leads.map((lead) => (
                                <div key={lead.lead_id} className="p-5 border-b border-ink/10 last:border-b-0" data-testid={`lead-${lead.lead_id}`}>
                                    <div className="flex items-start justify-between gap-4 flex-wrap">
                                        <div className="flex-1 min-w-[280px]">
                                            <div className="flex items-center gap-2 mb-1 text-xs">
                                                <span className="bg-ink text-white px-1.5 py-0.5 ui-heading uppercase tracking-wider">{lead.platform}</span>
                                                <span className="text-ink-muted">{lead.source_ref}</span>
                                                <span className="text-cobalt font-mono">{(lead.match_confidence || 0).toFixed(2)}</span>
                                                {lead.author && <span className="text-ink-muted">by {lead.author}</span>}
                                            </div>
                                            <a href={lead.url} target="_blank" rel="noreferrer noopener" className="font-display font-bold tracking-tight text-lg leading-tight hover:underline inline-flex items-start gap-1.5" data-testid={`lead-title-${lead.lead_id}`}>
                                                {lead.title} <ExternalLink size={13} className="opacity-50 flex-shrink-0 mt-1" />
                                            </a>
                                            <p className="text-sm text-ink-soft mt-2 line-clamp-3">{lead.snippet}</p>
                                            <p className="text-xs text-ink-muted mt-2 italic">{lead.match_reasoning}</p>
                                        </div>
                                        <div className="flex flex-col gap-1.5 min-w-[160px]">
                                            <button onClick={() => draftAnswer(lead)} className="btn-brutal bg-cobalt text-white border border-cobalt px-3 py-1.5 text-xs ui-heading inline-flex items-center justify-center gap-1.5" data-testid={`lead-draft-${lead.lead_id}`}>
                                                <Sparkles size={12} /> Draft answer
                                            </button>
                                            {lead.status !== "reviewing" && <button onClick={() => setStatus(lead, "reviewing")} className="border border-ink/20 px-3 py-1.5 text-xs" data-testid={`lead-review-${lead.lead_id}`}>Mark reviewing</button>}
                                            {lead.status !== "replied" && <button onClick={() => setStatus(lead, "replied")} className="border border-ink/20 px-3 py-1.5 text-xs" data-testid={`lead-replied-${lead.lead_id}`}>Mark replied</button>}
                                            {lead.status !== "ignored" && <button onClick={() => setStatus(lead, "ignored")} className="border border-ink/20 px-3 py-1.5 text-xs text-ink-muted" data-testid={`lead-ignore-${lead.lead_id}`}>Ignore</button>}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    <p className="text-xs text-ink-muted mt-4">Showing {leads.length} leads · {statusFilter} · ≥{minConf.toFixed(1)} confidence</p>
                </div>
            </section>
        </>
    );
}
